import React, { createContext, ReactNode, useReducer, useContext } from 'react';
import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MAX_QUESTIONS } from '~/App/Modules/Testimonials/Pages/Editor/Panels/Questions';

export enum TestimonialResponseState {
  Initial = 'initial',
  Changed = 'changed',
  Uploading = 'uploading',
  Uploaded = 'uploaded',
}

interface TestimonialResponseContextProps {
  questionNumber: number;
  state: TestimonialResponseState;
}

const getInitialState = (): TestimonialResponseContextProps[] => {
  const state: TestimonialResponseContextProps[] = [];
  for (
    let questionNumber = 1;
    questionNumber <= MAX_QUESTIONS;
    questionNumber++
  ) {
    state.push({
      questionNumber,
      state: TestimonialResponseState.Initial,
    });
  }

  return state;
};

const testimonialResponseSlice = createSlice({
  name: 'testimonialResponse',
  initialState: getInitialState(),
  reducers: {
    updateTestimonialResponseState(
      state: TestimonialResponseContextProps[],
      action: PayloadAction<TestimonialResponseContextProps>
    ) {
      state[action.payload.questionNumber - 1] = action.payload;
    },
  },
});

export const Actions = testimonialResponseSlice.actions;
const reducer = testimonialResponseSlice.reducer;

export const TestimonialResponseContext = createContext<
  | {
      state: TestimonialResponseContextProps[];
      dispatch: React.Dispatch<AnyAction>;
    }
  | undefined
>(undefined);

export function QuestionnaireResponseProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, getInitialState());

  return (
    <TestimonialResponseContext.Provider value={{ state, dispatch }}>
      {children}
    </TestimonialResponseContext.Provider>
  );
}

export function useTestimonialResponse() {
  const context = useContext(TestimonialResponseContext);
  if (context === undefined) {
    throw new Error(
      'useTestimonialResponse must be used within a TestimonialResponseContext.Provider'
    );
  }
  return context;
}
